import React, { useEffect, useId, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Form, Input, Label, Modal } from '@shared/components';
import { normaliseVin, normaliseVrm } from '@features/vehiclesearch/schemas/additionalvehicledetails.schema';
import type { TerminalIntent } from '../hooks/useVehicleIdentityGate';
import type { VehicleIdentity } from '../utils/vehicleIdentity';
import {
    VEHICLE_IDENTITY_REQUIRED,
    vehicleIdentitySchema,
    type VehicleIdentityFormInputs,
} from '../schemas/vehicleIdentity.schema';

interface VehicleIdentityMopUpModalProps {
    isOpen: boolean;
    /** Drives the primary label only; the gate owns what actually runs. */
    intent: TerminalIntent | null;
    isSaving: boolean;
    onSubmit: (identity: VehicleIdentity) => void;
    onClose: () => void;
}

const PRIMARY_LABEL: Record<TerminalIntent, string> = {
    complete: 'Save and complete quote',
    checkout: 'Save and continue to payment',
};

// Collects the missing VIN/VRM so the terminal action the user pressed can proceed against a
// stored, identified document. Built on the shared Modal and the existing field pattern.
export const VehicleIdentityMopUpModal: React.FC<VehicleIdentityMopUpModalProps> = ({
    isOpen,
    intent,
    isSaving,
    onSubmit,
    onClose,
}) => {
    const headingId = useId();
    const vrmMessageId = `${headingId}-vrm-message`;
    const vinMessageId = `${headingId}-vin-message`;
    const crossFieldMessageId = `${headingId}-identity-required`;
    // Input forwards no ref, so this uses the span-wrapper pattern, same as ConfirmActionModal.
    const vrmWrapperRef = useRef<HTMLSpanElement>(null);

    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm<VehicleIdentityFormInputs>({
        resolver: zodResolver(vehicleIdentitySchema),
    });

    // Modal unmounts its CHILDREN on close but this component stays mounted, so without an
    // explicit reset, Cancel then reopen would redisplay abandoned values and their errors.
    useEffect(() => {
        if (isOpen) reset();
    }, [isOpen, reset]);

    // Value and message read from the SAME watch() snapshot, never field.value: field.value
    // resurrects a value react-hook-form has already cleared internally.
    const watchedValues = watch();

    // The superRefine issue's path is ['vrm'], so RHF surfaces it as errors.vrm too. Distinguish
    // it from a genuine VRM length issue by message identity so the two never compete for one slot.
    const isCrossFieldIssue = errors.vrm?.message === VEHICLE_IDENTITY_REQUIRED;
    const vrmFieldMessage = errors.vrm && !isCrossFieldIssue ? errors.vrm.message : undefined;
    const crossFieldMessage = isCrossFieldIssue ? errors.vrm?.message : undefined;
    const vinFieldMessage = errors.vin?.message;

    // The VIN input's aria-invalid is derived from the cross-field message's presence,
    // not from errors.vin — nothing ever sets errors.vin for the cross-field issue.
    const vrmInvalid = !!(vrmFieldMessage || crossFieldMessage);
    const vinInvalid = !!(vinFieldMessage || crossFieldMessage);

    const vrmDescribedBy =
        [vrmFieldMessage ? vrmMessageId : undefined, crossFieldMessage ? crossFieldMessageId : undefined]
            .filter(Boolean)
            .join(' ') || undefined;
    const vinDescribedBy =
        [vinFieldMessage ? vinMessageId : undefined, crossFieldMessage ? crossFieldMessageId : undefined]
            .filter(Boolean)
            .join(' ') || undefined;

    // ConfirmActionModal discipline: Escape/backdrop/X must not dismiss a dialog mid-save.
    const handleClose = () => {
        if (isSaving) return;
        onClose();
    };

    const handleValid = (values: VehicleIdentityFormInputs) => {
        onSubmit({ vin: values.vin, vrm: values.vrm });
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            showCloseButton={!isSaving}
            ariaLabelledBy={headingId}
            className="max-w-md p-6"
            initialFocusRef={vrmWrapperRef}
        >
            <h2 id={headingId} className="text-lg font-semibold mb-2">
                Vehicle identity needed
            </h2>
            <p className="text-sm text-mp-muted mb-4">
                Enter the vehicle&apos;s registration number or its VIN before continuing. Either one is
                enough.
            </p>

            {/* noValidate — the Form component's documented opt-in, so native constraint UI
                cannot pre-empt a Zod message. */}
            <Form onSubmit={handleSubmit(handleValid)} noValidate className="space-y-4">
                <div>
                    <Label htmlFor="mopup-vrm">Registration number</Label>
                    <Controller
                        name="vrm"
                        control={control}
                        render={({ field }) => (
                            <span ref={vrmWrapperRef}>
                                <Input
                                    type="text"
                                    id="mopup-vrm"
                                    value={watchedValues.vrm || ''}
                                    onChange={(e) => field.onChange(normaliseVrm(e.target.value))}
                                    error={vrmInvalid}
                                    aria-invalid={vrmInvalid}
                                    aria-describedby={vrmDescribedBy}
                                    placeholder="e.g. AB12 CDE"
                                    disabled={isSaving}
                                />
                            </span>
                        )}
                    />
                    {vrmFieldMessage && (
                        <p id={vrmMessageId} role="alert" className="mt-1.5 text-xs text-mp-error">
                            {vrmFieldMessage}
                        </p>
                    )}
                    {crossFieldMessage && (
                        <p id={crossFieldMessageId} role="alert" className="mt-1.5 text-xs text-mp-error">
                            {crossFieldMessage}
                        </p>
                    )}
                </div>

                <div>
                    <Label htmlFor="mopup-vin">VIN</Label>
                    <Controller
                        name="vin"
                        control={control}
                        render={({ field }) => (
                            <Input
                                type="text"
                                id="mopup-vin"
                                value={watchedValues.vin || ''}
                                onChange={(e) => field.onChange(normaliseVin(e.target.value))}
                                error={vinInvalid}
                                aria-invalid={vinInvalid}
                                aria-describedby={vinDescribedBy}
                                placeholder="Enter the 17-character VIN"
                                disabled={isSaving}
                            />
                        )}
                    />
                    {vinFieldMessage && (
                        <p id={vinMessageId} role="alert" className="mt-1.5 text-xs text-mp-error">
                            {vinFieldMessage}
                        </p>
                    )}
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="clear" disabled={isSaving} onClick={handleClose}>
                        Cancel
                    </Button>
                    {/* Never disabled for validity (Power of Ten #9's UI-gating discipline,
                        restated for forms): a user must be able to press it and hear why. */}
                    <Button type="submit" variant="primary" disabled={isSaving}>
                        {isSaving ? 'Saving…' : intent ? PRIMARY_LABEL[intent] : 'Save'}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};
